import {Alert, Button, CircularProgress, DialogActions} from "@mui/material";
import {ReactNode} from "react";

// Dialog footer with a busy-aware submit button and an error alert above the button row.
//
// Built to drop straight onto a useAsyncAction() / useBackgroundAction() result: those expose
// `loading` and `error: string | null` with the exact shapes these props expect, so wiring one
// up is a direct hand-off — pass the hook fields in and call its callback from onSubmit:
//
//   const save = useAsyncAction((input) => api.thing.upsert(input), []);
//   <SmoothActionBar
//       loading={save.loading}
//       error={save.error}
//       onSubmit={() => save.callback(input)}
//       onCancel={props.onCancel}
//   />
//
// This replaces the hook's own loading/error element (e.g. save.LoadingOrErrorElement) plus a
// hand-rolled cancel/submit pair — the spinner renders inside the submit button and the error
// as an alert, so don't also render that element separately.
export function SmoothActionBar(props: {
    // shows an indeterminate spinner in the submit button; while it (or destroyLoading) is set every
    // button is disabled
    loading?: boolean;
    // shown as an alert above the buttons; shared by the submit and destroy actions. Wire a hook's
    // `error` straight in, or pass a node to wrap the message with guidance (only rendered when truthy)
    error?: ReactNode;

    // omit to hide the submit button (e.g. a read-only variant of the dialog)
    onSubmit?: () => void;
    submitDisabled?: boolean;
    // submit button colour (defaults to "primary"); use "error" for a destructive confirm
    submitColor?: "primary" | "error";
    // submit button label when idle (defaults to "Save")
    actionName?: string;
    // submit button label while loading (defaults to "Saving…")
    submittingWord?: string;

    // omit to hide the cancel button
    onCancel?: () => void;
    cancelDisabled?: boolean;
    // cancel button label (defaults to "Cancel")
    cancelName?: string;

    // left-aligned destructive action (e.g. delete/archive); omit to hide it. Renders as a text
    // button in the error color with its own spinner (destroyLoading), but shares the error alert
    // above with submit
    onDestroy?: () => void;
    destroyLoading?: boolean;
    destroyDisabled?: boolean;
    // destroy button label when idle (defaults to "Delete")
    destroyName?: string;
    // destroy button label while loading (defaults to "Deleting…")
    destroyingWord?: string;

    // content rendered immediately left of the cancel/submit buttons
    leftOfButtons?: ReactNode;
    // content rendered at the start of the button row (left-aligned)
    children?: ReactNode;
}) {
    // either action being in-flight locks the whole bar
    const anyLoading = props.loading || props.destroyLoading;

    return (
        <DialogActions sx={{
            flexDirection: "column",
            alignItems: "stretch",
            gap: 1,
            borderTop: 1,
            borderColor: "divider",
            pt: 2,
            pl: 3,
            "& > :not(:first-of-type)": {ml: 0},
        }}>
            {props.error &&
                <Alert severity="error">
                    {props.error}
                </Alert>}

            <div style={{display: "flex", alignItems: "center", gap: 8}}>
                {props.onDestroy &&
                    <Button
                        color="error"
                        disabled={anyLoading || props.destroyDisabled}
                        onClick={props.onDestroy}
                        startIcon={props.destroyLoading ? <CircularProgress size={16} color="inherit"/> : undefined}
                    >
                        {props.destroyLoading ? (props.destroyingWord || "Deleting…") : (props.destroyName || "Delete")}
                    </Button>}

                {props.children}

                <div style={{flex: 1}} />

                {props.leftOfButtons}

                {props.onCancel && <Button onClick={props.onCancel} disabled={anyLoading || props.cancelDisabled}>{props.cancelName || "Cancel"}</Button>}
                {props.onSubmit &&
                    <Button
                        disabled={anyLoading || props.submitDisabled}
                        variant="contained" color={props.submitColor || "primary"} onClick={props.onSubmit}
                        startIcon={props.loading ? <CircularProgress size={16} color="inherit"/> : undefined}
                    >
                        {props.loading ? (props.submittingWord || "Saving…") : (props.actionName || "Save")}
                    </Button>}
            </div>
        </DialogActions>
    )
}